import {
  Configuration,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

/**
 * MSAL configuration for the Super-Admin SPA.
 *
 * Key differences from the customer admin SPA (viq-admin/src/auth/msalConfig.ts):
 *   - Authority: workforce Entra tenant (fae70d28-...), NOT the CIAM tenant.
 *     Standard Entra needs no `knownAuthorities` override, and production
 *     passes none. Only local dev sets one — see SUPERADMIN_KNOWN_AUTHORITY.
 *   - Scope: `api://<superadmin-client-id>/superadmin_access` (Task 1 app reg).
 *   - The id-token `roles` claim carries the app roles assigned in Entra
 *     (SuperAdmin / SuperAdminReadOnly). AbilityContext.tsx reads these.
 *
 * Values come from env vars (Vite, selected by --mode): `.env.production` for
 * the deploy build, `.env.offline` for the local dev server. The hardcoded fallbacks match
 * the workforce tenant so the app still boots if a var is accidentally missing.
 */

function env(name: string, fallback?: string): string {
  const value = (import.meta.env[name] as string | undefined) ?? fallback;
  if (!value) {
    throw new Error(
      `Missing required env var ${name}. Run \`npm run dev:offline\` for local dev, or build with \`npm run build\` (configs in .env.offline / .env.production).`,
    );
  }
  return value;
}

export const SUPERADMIN_TENANT_ID = env(
  'VITE_SUPERADMIN_TENANT_ID',
  'fae70d28-017a-40f9-89a9-6774f0624141',
);

export const SUPERADMIN_CLIENT_ID = env('VITE_SUPERADMIN_CLIENT_ID');

export const SUPERADMIN_AUTHORITY = env(
  'VITE_SUPERADMIN_AUTHORITY',
  `https://login.microsoftonline.com/${SUPERADMIN_TENANT_ID}`,
);

/**
 * Local-dev only. Deliberately optional, with NO fallback — unlike the vars
 * above — so production never passes `knownAuthorities` at all.
 *
 * MSAL resolves cloud-discovery metadata config-first: when the authority's
 * host is listed in `knownAuthorities` it builds metadata from that single
 * host and stops, never reaching its hardcoded values. For
 * `login.microsoftonline.com` those hardcoded values carry Microsoft's full
 * alias set (login.windows.net, login.microsoft.com, sts.windows.net, …), so
 * passing even the host we already point at would narrow production's aliases
 * to one. Leaving the option out entirely keeps production on exactly the path
 * it takes today.
 *
 * A local dev issuer's host is in no metadata, so MSAL
 * rejects it unless it is declared here. `.env.offline` sets this; nothing
 * else should.
 */
export const SUPERADMIN_KNOWN_AUTHORITY = import.meta.env
  .VITE_SUPERADMIN_KNOWN_AUTHORITY as string | undefined;

export const API_SCOPE = `api://${SUPERADMIN_CLIENT_ID}/superadmin_access`;

const msalConfig: Configuration = {
  auth: {
    clientId: SUPERADMIN_CLIENT_ID,
    authority: SUPERADMIN_AUTHORITY,
    // Omitted unless set, so production's auth config is unchanged. See above.
    ...(SUPERADMIN_KNOWN_AUTHORITY
      ? { knownAuthorities: [SUPERADMIN_KNOWN_AUTHORITY] }
      : {}),
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message) => {
        if (level <= LogLevel.Warning) {
          // eslint-disable-next-line no-console
          console.warn(`[msal-superadmin] ${message}`);
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Pull a fresh access token for the superadmin scope.
 * Silent first; falls back to interactive redirect when refresh fails.
 * Returned tokens are meant for one request — don't cache.
 */
export async function acquireSuperAdminToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // Caller should be inside <AuthenticatedTemplate>. If not, kick to login.
    await msalInstance.loginRedirect({ scopes: [API_SCOPE] });
    throw new Error('Redirecting to login — no active account');
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      account: accounts[0],
      scopes: [API_SCOPE],
    });
    return result.accessToken;
  } catch {
    await msalInstance.acquireTokenRedirect({ scopes: [API_SCOPE] });
    // acquireTokenRedirect navigates away; unreachable but satisfies return type.
    throw new Error('Redirecting to acquire fresh token');
  }
}
